import { useMemo } from 'react';
import { debounce } from 'lodash-es';

import { Flex, Heading, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SectionHeader } from '~/components/section-header';
import { trackEvent } from '~/features/analytics/track-event';
import { TestimonialCarousel } from '~/features/testimonials/components/testimonial-carousel';
import { homepageTestimonials } from '~/features/testimonials/data/homepage-testimonials';

import { SCHOOL_LP_ANALYTICS_EVENT } from '../constants';
import { CheckedIcon } from '../icons';
import { SchoolLpContent } from '../models/school-lp-content';

type SchoolTestimonialsSectionProps = NonNullable<StaticSchoolTestimonialsSectionProps>;

export function SchoolTestimonialsSection({ eyebrow, title, description }: SchoolTestimonialsSectionProps) {
  const handleInteraction = useMemo(
    () =>
      debounce(() => {
        trackEvent({
          name: SCHOOL_LP_ANALYTICS_EVENT,
          type: 'click',
          label: 'testimonial_scroll_clicked',
        });
      }, 100),
    []
  );

  return (
    <Flex
      as='section'
      bg='white'
      flexDirection='column'
      gap={{ base: '40px', md: '48px' }}
      paddingY={{ base: '64px', md: '96px' }}
    >
      <ContentContainer margin='0 auto' width='100%'>
        <SectionHeader
          color='grey.500'
          iconSlot={<CheckedIcon boxSize='18px' color='grey.500' />}
          subtitle={eyebrow}
          descriptionSlot={
            <Text color='grey.500' fontSize={{ base: '0.875rem', md: '1rem' }} lineHeight='1.5'>
              {description}
            </Text>
          }
          titleSlot={
            <Heading as='h2' color='grey.900' size={{ base: 'mobile-heading-2', md: 'heading-2' }}>
              {title}
            </Heading>
          }
        />
      </ContentContainer>
      <TestimonialCarousel initialSlide={1} testimonials={homepageTestimonials} onInteraction={handleInteraction} />
    </Flex>
  );
}

export function transformStaticSchoolTestimonialsSectionProps({ content }: { content: SchoolLpContent }) {
  if (!content.testimonials) {
    return null;
  }

  return content.testimonials;
}

export type StaticSchoolTestimonialsSectionProps = ReturnType<typeof transformStaticSchoolTestimonialsSectionProps>;
